import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { AllRoleTypes, CreateUserRequest } from "api/types";
import { StyledButton } from "components/StyledButton";
import { isValidEmail } from "helpers/authValidation";
import { useAuthStatus } from "helpers/useAuthStatus";
import { useCallback, useEffect, useState } from "react";

type AccountDraft = CreateUserRequest & { userId: string };

export interface CreateAccountsPageDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (account: AccountDraft) => void;
    user: AccountDraft;
}

interface Event {
    target: {
        name: string;
        value: string;
    };
}

export const CreateAccountsPageDialog = ({
    open,
    onClose,
    onSubmit,
    user,
}: CreateAccountsPageDialogProps): JSX.Element => {
    const { role: loggedInRole } = useAuthStatus();
    const [account, setAccount] = useState<AccountDraft>(user);

    useEffect(() => {
        setAccount(user);
    }, [user]);

    const availableRoles = AllRoleTypes.filter((r) => {
        if (r === "admin") return false;
        if (r === "admin-teacher" && loggedInRole === "teacher") return false;
        return true;
    });

    const handleChange = (event: Event): void => {
        const { name, value } = event.target;
        setAccount({ ...account, [name]: value });
    };

    const handleSelectChange = (event: Event): void => {
        setAccount({
            ...account,
            role: event.target.value
                .split(",")
                .map((role) => role.trim())
                .filter((role) => role !== "") as CreateUserRequest["role"],
        });
    };

    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");

    const isFormValid = useCallback(() => {
        if (account.firstName.trim().length === 0) {
            setFirstNameError("First name is required");
            return false;
        } else {
            setFirstNameError("");
        }

        if (account.lastName.trim().length === 0) {
            setLastNameError("Last name is required");
            return false;
        } else {
            setLastNameError("");
        }

        if (account.email.trim().length === 0) {
            setEmailError("Email is required");
            return false;
        } else {
            setEmailError("");
        }

        if (!isValidEmail(account.email)) {
            setEmailError("Invalid email");
            return false;
        } else {
            setEmailError("");
        }

        if (account.password.trim().length === 0) {
            setPasswordError("Password is required");
            return false;
        } else {
            setPasswordError("");
        }

        return true;
    }, [account]);

    const handleSubmit = (): void => {
        if (!isFormValid()) {
            return;
        }

        onSubmit(account);
        onClose();
        setAccount({
            userId: Date.now().toString(),
            firstName: "",
            lastName: "",
            email: "",
            role: ["student"],
            password: "",
        });
    };

    return (
        <Dialog open={open} onClose={onClose} aria-labelledby="add account dialog">
            <DialogTitle id="add account dialog">Add Account</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    id="firstName"
                    label="First Name"
                    type="text"
                    fullWidth
                    name="firstName"
                    value={account.firstName}
                    onChange={handleChange}
                    error={firstNameError.length > 0}
                    helperText={firstNameError}
                />
                <TextField
                    margin="dense"
                    id="lastName"
                    label="Last Name"
                    type="text"
                    fullWidth
                    name="lastName"
                    value={account.lastName}
                    onChange={handleChange}
                    error={lastNameError.length > 0}
                    helperText={lastNameError}
                />
                <TextField
                    margin="dense"
                    id="email"
                    label="Email"
                    type="email"
                    fullWidth
                    name="email"
                    value={account.email}
                    onChange={handleChange}
                    error={emailError.length > 0}
                    helperText={emailError}
                />
                <TextField
                    margin="dense"
                    id="password"
                    label="Initial Password"
                    type="password"
                    fullWidth
                    name="password"
                    value={account.password}
                    onChange={handleChange}
                    error={passwordError.length > 0}
                    helperText={passwordError}
                />
                <Select
                    id="role"
                    name="role"
                    margin="dense"
                    value={account.role.join(", ")}
                    label="Role"
                    onChange={handleSelectChange}
                    fullWidth
                    sx={{ marginTop: "1rem" }}
                >
                    {availableRoles.map((v) => {
                        const text =
                            v === "admin-teacher" ? "Teacher with Admin Privileges" : v;
                        return (
                            <MenuItem key={v} value={v}>
                                {text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </DialogContent>
            <DialogActions>
                <StyledButton onClick={onClose}>Cancel</StyledButton>

                <StyledButton variant="contained" onClick={handleSubmit}>
                    Add Account
                </StyledButton>
            </DialogActions>
        </Dialog>
    );
};
